import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Gift, removedGiftForProductEvent, selectGifts, selectOneGiftForProduct } from '@/entities/cart'
import { toggleAutoGiftsForProduct } from '@/entities/cart'
import { selectProductGiftConfig } from '@/entities/cart/selectors'

type UseProductGiftForBasketItemProps = {
	productId: number
}

type UseProductGiftForBasketItemResult = {
	oneGifts: Gift[]
	allGifts: Gift[]
	selectedGift: Gift | null
	removeGift: () => void
	selectedGiftCount: number
	isAutoGiftsExcluded: boolean
	excludeAutoGifts: () => void
	includeAutoGifts: () => void
	excludeAutoGift: (giftId: number) => void
	includeAutoGift: (giftId: number) => void
	allPackages: Gift[][]
	selectedPackageIndex: number | null
	hasMultiplePackages: boolean
}

/**
 * НОВАЯ АРХИТЕКТУРА: Изолированная логика подарков для товара в корзине
 *
 * Читает данные из ProductGiftConfig - все данные для конкретного товара изолированы.
 * Нет batch API, нет фильтрации, нет пересечений между товарами.
 */
export const useProductGiftForBasketItem = (
	props: UseProductGiftForBasketItemProps
): UseProductGiftForBasketItemResult => {
	const { productId } = props
	const dispatch = useDispatch()

	// Читаем изолированную конфигурацию для этого товара
	const config = useSelector(selectProductGiftConfig(productId))

	// Читаем выбранный подарок из старой системы (для совместимости)
	const selectedGift = useSelector((state: any) => selectGifts.selectById(state, productId))

	// Обработчики - ВАЖНО: создаем ДО раннего return чтобы React не ругался
	const removeGift = useCallback(() => {
		if (selectedGift) {
			dispatch(removedGiftForProductEvent({ gift: selectedGift }))
			// ВАЖНО: также очищаем selectedGift в новой архитектуре
			dispatch(selectOneGiftForProduct({ productId, gift: null }))
		}
	}, [selectedGift, dispatch, productId])

	const excludeAutoGifts = useCallback(() => {
		dispatch(toggleAutoGiftsForProduct({ productId, excluded: true }))
	}, [productId, dispatch])

	const includeAutoGifts = useCallback(() => {
		dispatch(toggleAutoGiftsForProduct({ productId, excluded: false }))
	}, [productId, dispatch])

	if (!config) {
		return {
			oneGifts: [],
			allGifts: [],
			selectedGift: null,
			removeGift,
			selectedGiftCount: 1,
			isAutoGiftsExcluded: false,
			excludeAutoGifts,
			includeAutoGifts,
			excludeAutoGift: () => {},
			includeAutoGift: () => {},
			allPackages: [],
			selectedPackageIndex: null,
			hasMultiplePackages: false
		}
	}

	// Формируем результат в зависимости от типа
	let result

	if (config.type === 'one') {
		// Подарки на выбор
		result = {
			oneGifts: config.availableOneGifts,
			allGifts: [],
			selectedGift: config.selectedGift || selectedGift,
			removeGift,
			selectedGiftCount: 1,
			isAutoGiftsExcluded: false,
			excludeAutoGifts,
			includeAutoGifts,
			excludeAutoGift: () => {},
			includeAutoGift: () => {},
			allPackages: [],
			selectedPackageIndex: null,
			hasMultiplePackages: false
		}
	} else if (config.type === 'packages') {
		// Пакеты подарков
		const selectedIndex = config.selectedPackageIndex
		const selectedPackageGifts = selectedIndex !== null ? config.availablePackages[selectedIndex] || [] : []

		result = {
			oneGifts: [],
			allGifts: selectedPackageGifts,
			selectedGift: null,
			removeGift: () => {},
			selectedGiftCount: 1,
			isAutoGiftsExcluded: config.autoGiftsExcluded,
			excludeAutoGifts,
			includeAutoGifts,
			excludeAutoGift: () => {},
			includeAutoGift: () => {},
			allPackages: config.availablePackages,
			selectedPackageIndex: selectedIndex,
			hasMultiplePackages: config.availablePackages.length > 1
		}
	} else {
		// Type: 'auto' (пока не реализовано)
		console.log(`  Type: AUTO (not implemented)`)
		result = {
			oneGifts: [],
			allGifts: [],
			selectedGift: null,
			removeGift: () => {},
			selectedGiftCount: 1,
			isAutoGiftsExcluded: config.autoGiftsExcluded,
			excludeAutoGifts,
			includeAutoGifts,
			excludeAutoGift: () => {},
			includeAutoGift: () => {},
			allPackages: [],
			selectedPackageIndex: null,
			hasMultiplePackages: false
		}
	}
	return result
}
